import { type MutableRefObject, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { DropdownItem, ItemIcon } from '@elfsight/widget-modal';
import type { CountryCode as CountryCodeType } from 'libphonenumber-js';
import type { Country } from '../countries';
import { IconWithOverlay } from './IconWithOverlay';

interface CountryListProps {
  countries: readonly Country[];
  onSelect: (country: Country) => void;
  onCloseRequest: () => void;
  fieldColor: string;
  scrollContainer: MutableRefObject<null | HTMLDivElement>;
  selectedCountryCode: CountryCodeType;
  emptyCountryListMessage: string;
}

const ITEM_CONTAINER_CLIENT_HEIGHT = 38;

export const CountryList = ({
  countries,
  onSelect,
  onCloseRequest,
  selectedCountryCode,
  scrollContainer,
  fieldColor,
  emptyCountryListMessage
}: CountryListProps) => {
  const selectedElement = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedElement.current || !scrollContainer.current) {
      return;
    }

    const selectedItemContainerParent = selectedElement.current.parentNode
      ?.parentNode as HTMLElement | null;
    const selectedOffsetTop = selectedItemContainerParent?.offsetTop;

    const scrollContainerParent = scrollContainer.current
      .offsetParent as HTMLElement | null;
    const scrollContainerParentOffsetHeight =
      scrollContainerParent?.offsetHeight;

    if (!selectedOffsetTop || !scrollContainerParentOffsetHeight) {
      return;
    }

    const scrollTopValue =
      selectedOffsetTop -
      scrollContainerParentOffsetHeight / 2 +
      ITEM_CONTAINER_CLIENT_HEIGHT / 2;

    const rafID = requestAnimationFrame(() => {
      scrollContainer?.current?.scrollTo({ top: scrollTopValue });
    });

    return () => cancelAnimationFrame(rafID);
  }, [scrollContainer, selectedCountryCode]);

  if (countries.length === 0) {
    return (
      <EmptyMessage _color={fieldColor}>{emptyCountryListMessage}</EmptyMessage>
    );
  }

  return countries.map((country) => (
    <StyledDropdownItem
      key={`${country.countryCode}-${country.name}`}
      _highlightColor={fieldColor}
      iconLeft={() => (
        <IconWithOverlay>
          <StyledIcon src={country.iconSrc} size={[24, 18]} />
        </IconWithOverlay>
      )}
      onClick={() => {
        onSelect(country);
        onCloseRequest();
      }}
    >
      <CountryItem
        ref={(element) => {
          if (country.countryCode === selectedCountryCode)
            selectedElement.current = element;
        }}
      >
        <CountryName _color={fieldColor}>{country.name}</CountryName>
        <CountryCode
          _color={fieldColor}
        >{`(${country.phoneCode})`}</CountryCode>
      </CountryItem>
    </StyledDropdownItem>
  ));
};

const StyledDropdownItem = styled(DropdownItem)<{ _highlightColor: string }>`
  padding: 8px 16px;
  padding-right: 10px;
  transition: background-color 0.1s ease-in-out;
  &:hover {
    background-color: ${({ _highlightColor }) =>
      getColorWithAlpha(_highlightColor, 0.05)};
  }

  & > * {
    height: 20px;
  }

  & > div:nth-child(1) {
    width: fit-content;
    padding-top: 0;
    height: 18px;
  }

  & > div:nth-child(2) {
    overflow: hidden;
  }

  & svg {
    border-radius: 2px;
    width: 24px;
    height: 18px;
    outline: 1px solid rgba(118, 118, 118, 0.2) !important;
    outline-offset: -1px;
  }
`;

const StyledIcon = styled(ItemIcon)`
  padding: 0 !important;

  & svg {
    fill: none;
  }
`;

const CountryItem = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CountryCode = styled.span<{ _color: string }>`
  margin-left: 4px;
  color: ${({ _color }) => getColorWithAlpha(_color, 0.5)};
`;

const CountryName = styled.span<{ _color: string }>`
  color: ${({ _color }) => _color};
  text-wrap: nowrap;
  text-overflow: ellipsis;
  max-width: 180px;
  overflow: hidden;
`;

const EmptyMessage = styled.div<{ _color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  color: ${({ _color }) => getColorWithAlpha(_color, 0.5)};
`;
